import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountSlice } from "../../redux/slices/accountSlice";
import ListTabs from "../DropdownsAndTabs/ListTabs";
import { Colors } from "../../utils/colors";
import {
  GraphTimelineTabs,
  TimelineLabels,
  getGraphTimelineDate,
  timelineStartDate,
  graphTimelineBuckets,
  toFixedNumber,
  convertToMoney,
} from "@markit/common.utils";
import { CircularProgress } from "@mui/material";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer } from "recharts";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { Icon } from "@iconify/react";
import {
  getStripeGrossVolume,
  testStripe,
} from "../../utils/stripeAccountUtils";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { StripeAccountStatus } from "@markit/common.types";
import PopupModalMessageTag from "../Containers/PopupModalMessageTag";
import EnablePaidTicketsModal from "../DisplayModal/EnablePaidTicketsModal";

type RevenueData = {
  grossVolume: number[];
  total: number;
  previousTotal: number;
};

export const RevenueGraphContainer = () => {
  const { accountData, appInitialized, readyForPayment } =
    useSelector(getAccountSlice);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(1);
  const [loadingData, setLoadingData] = useState(false);
  const [revenueData, setRevenueData] = useState<Map<string, RevenueData>>(
    new Map<TimelineLabels, RevenueData>()
  );
  const [stripeVisible, setStripeVisible] = useState(false);

  const styles = {
    smallText: { fontSize: 12, color: Colors.GRAY2 },
    timelineBorder: {
      borderLeft: "0.5px solid #929292",
      borderBottom: "0.5px solid #929292",
      borderRight: "0.5px solid #929292",
    },
    tabContainer: {
      borderRadius: 100,
      width: "20%",
      justifyContent: "center",
    },
  };

  const stripeAccountId = useMemo(
    () =>
      testStripe(accountData.uid)
        ? accountData.testStripeAccountId || ""
        : accountData.stripeAccountId,
    [
      accountData.uid,
      accountData.stripeAccountId,
      accountData.testStripeAccountId,
    ]
  );

  const userStripeAccountStatus = useMemo(
    () =>
      testStripe(accountData.uid)
        ? accountData.testStripeAccountStatus
        : accountData.stripeAccountStatus,
    [
      accountData.uid,
      accountData.stripeAccountStatus,
      accountData.testStripeAccountStatus,
    ]
  );

  const awaitingManualReview = useMemo(
    () => userStripeAccountStatus === StripeAccountStatus.MANUAL_REVIEW,
    [userStripeAccountStatus]
  );

  const selectedTab = useMemo(
    () => GraphTimelineTabs[selectedTabIndex],
    [selectedTabIndex]
  );

  const startDate = useMemo(
    () =>
      timelineStartDate(
        selectedTab,
        accountData.completedCreatorSetupTimestamp
      ),
    [accountData.completedCreatorSetupTimestamp, selectedTab]
  );

  const currentRevenueData = revenueData.get(selectedTab);

  const revenueToShow = useMemo(() => {
    if (loadingData) {
      return undefined;
    }
    if (currentRevenueData) {
      return currentRevenueData.grossVolume.map((amount, index) => ({
        name: index.toString(),
        value: amount / 100,
      }));
    }
    return [];
  }, [currentRevenueData, loadingData]);

  const percentChange = useMemo(() => {
    if (!currentRevenueData || currentRevenueData.previousTotal <= 0) {
      return undefined;
    }
    return toFixedNumber(
      ((currentRevenueData.total - currentRevenueData.previousTotal) /
        currentRevenueData.previousTotal) *
        100,
      1
    );
  }, [currentRevenueData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (!readyForPayment || revenueData.has(selectedTab)) {
      return;
    }
    setLoadingData(true);
    try {
      const buckets = graphTimelineBuckets(selectedTab, startDate);
      const bucketStarts = buckets.map((bucket) =>
        Math.floor(bucket.getTime() / 1000)
      );
      const endTime = Math.floor(Date.now() / 1000);
      // Previous period is the same duration immediately before the selected range
      const previousStartTime =
        selectedTab === TimelineLabels.ALL
          ? undefined
          : bucketStarts[0] - (endTime - bucketStarts[0]);

      const { grossVolume, total, previousTotal } = await getStripeGrossVolume(
        accountData.uid,
        stripeAccountId,
        bucketStarts,
        endTime,
        previousStartTime
      );
      revenueData.set(selectedTab, { grossVolume, total, previousTotal });
      setRevenueData(new Map(revenueData));
    } catch (error) {
      console.error(error);
      revenueData.set(selectedTab, {
        grossVolume: [],
        total: -1,
        previousTotal: -1,
      });
      setRevenueData(new Map(revenueData));
    }
    setLoadingData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTabIndex, stripeAccountId, readyForPayment]);

  const renderBody = () => {
    if (loadingData || !appInitialized) {
      return (
        <div className="Centering" style={{ paddingTop: 50 }}>
          <CircularProgress
            style={{ color: Colors.GRAY1, alignSelf: "center" }}
            size={18}
          />
        </div>
      );
    }

    if (!readyForPayment) {
      const title =
        stripeAccountId === ""
          ? "Create Stripe Account"
          : awaitingManualReview
            ? "Awaiting Manual Review from our Team"
            : "Finish Connecting Stripe";
      const description =
        stripeAccountId === ""
          ? "To start monetizing your audience on Markit"
          : awaitingManualReview
            ? "Our team is reviewing your Stripe account"
            : "Complete your Stripe setup to start monetizing your audience";

      return (
        <StandardBorderedContainer
          containerStyles={{ backgroundColor: Colors.WHITE1, padding: 14 }}
          onPress={() => setStripeVisible(true)}
        >
          <div className="AlignedRowSpaced">
            <div className="AlignedRow" style={{ gap: 14 }}>
              <Icon
                icon="fa:cc-stripe"
                height={32}
                style={{ color: Colors.PURPLE11 }}
              />
              <div className="ColumnNormal" style={{ gap: 2 }}>
                <span className="bodyMedium">{title}</span>
                <span
                  className="bodySmallRegular"
                  style={{ color: Colors.GRAY1 }}
                >
                  {description}
                </span>
              </div>
            </div>
            <Icon
              icon="ion:chevron-right"
              height={14}
              style={{ color: Colors.GRAY2 }}
            />
          </div>
        </StandardBorderedContainer>
      );
    }

    return (
      <div className="ColumnNormal" style={{ gap: 7 }}>
        <div className="ColumnNormal">
          <div>
            <span style={{ fontSize: 24 }}>
              {convertToMoney((currentRevenueData?.total ?? 0) / 100 || 0)}
            </span>
            {percentChange !== undefined &&
            selectedTab !== TimelineLabels.ALL ? (
              <span
                style={{
                  ...styles.smallText,
                  color: percentChange >= 0 ? Colors.GREEN2 : Colors.RED3,
                  marginLeft: 4,
                }}
              >
                {percentChange >= 0 ? "+" : ""}
                {percentChange}%
              </span>
            ) : null}
          </div>
          <div className="AlignedRowSpaced">
            <PopupModalMessageTag
              message=""
              alternateMessageComp={
                <div className="AlignedRow" style={{ gap: 4 }}>
                  <span className="smallBodySubtext">Net Volume</span>
                  <Icon
                    icon="mdi:information-outline"
                    height={12}
                    style={{ color: Colors.GRAY1, paddingTop: 1 }}
                  />
                </div>
              }
              popupTitle=""
              popupSubtext={
                <span
                  className="bodySmallRegular"
                  style={{ color: Colors.BLACK }}
                >
                  Estimated volume from payments settled to your Stripe balance,
                  after fees are deducted. Shows the percent change from the
                  same duration in the previous time range.
                </span>
              }
              popupPosition="top"
              onHover
            />
            <span style={styles.smallText}>
              {getGraphTimelineDate(startDate)} - Today
            </span>
          </div>
        </div>
        {currentRevenueData?.total === -1 ? (
          <div
            className="Centering"
            style={{ ...styles.timelineBorder, height: 64 }}
          >
            <span style={styles.smallText}>
              Revenue is temporarily unavailable. Try again in a few minutes.
            </span>
          </div>
        ) : revenueToShow &&
          revenueToShow.length > 0 &&
          currentRevenueData?.total ? (
          <ResponsiveContainer style={styles.timelineBorder} height={64}>
            <LineChart
              data={revenueToShow}
              margin={{ left: 0, right: 0, bottom: 0, top: 4 }}
            >
              <XAxis hide />
              <YAxis hide />
              <Line
                dot={false}
                dataKey="value"
                stroke={Colors.BLUE5}
                strokeWidth={1.5}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div
            className="Centering"
            style={{ ...styles.timelineBorder, height: 64 }}
          >
            <span style={styles.smallText}>
              No revenue found within the time range
            </span>
          </div>
        )}
      </div>
    );
  };

  const isEmptyState = !loadingData && appInitialized && !readyForPayment;

  return (
    <>
      <div
        className="ColumnNormal"
        style={{ paddingInline: 14, gap: 6, height: "100%" }}
      >
        <ListTabs
          tabLabels={GraphTimelineTabs}
          tabNumbers={[-1, -1, -1, -1, -1]}
          selectedValue={selectedTabIndex}
          onChange={(index: number) => setSelectedTabIndex(index)}
          containerStyles={{ justifyContent: "space-between" }}
          tabStyles={{
            ...styles.tabContainer,
            backgroundColor: Colors.WHITE,
            border: "none",
          }}
          selectedTabStyles={{
            ...styles.tabContainer,
            backgroundColor: Colors.WHITE1,
            border: `0.5px solid ${Colors.GRAY2}`,
          }}
          altColor={Colors.WHITE1}
          newTextColor={Colors.GRAY3}
          newSelectedTextColor={Colors.BLACK}
        />
        <div
          className="ColumnNormal"
          style={{
            gap: 7,
            flex: 1,
            minHeight: 0,
            justifyContent: isEmptyState ? "flex-end" : "flex-start",
          }}
        >
          {renderBody()}
        </div>
      </div>
      {stripeVisible ? (
        <EnablePaidTicketsModal
          isVisible={stripeVisible}
          setIsVisible={setStripeVisible}
        />
      ) : null}
    </>
  );
};
